/**
 * §9's "dark mode default; both themes are token swaps", as the two platform bits a token cannot
 * carry: what the reader chose, and what their system prefers.
 *
 * The swap itself is one attribute — `data-theme` on `<html>` — because that is what
 * `@visious/tokens` generates against. Nothing else in the app knows a theme exists.
 *
 * **Stamped before the first paint** (from `main.tsx`, not from an effect). React renders after this
 * runs, so a light-theme reader never sees a dark frame; an effect would put the swap one frame
 * late, which reads as a flash on every page load.
 *
 * The *choice* is `'dark'` by default (§9's dark default, taken literally) and the toggle in
 * the shell is a plain dark↔light flip. The settings page is where `'system'` is chosen (§8's
 * preference-sync table has `dark_mode`), so the storage shape carries all three.
 */

const KEY = 'visious.theme';

export type ThemeChoice = 'system' | 'dark' | 'light';
export type ResolvedTheme = 'dark' | 'light';

/** Pure: the whole decision, so a test or a TV can make it without a `window`. */
export function resolveTheme(choice: ThemeChoice, systemPrefersLight: boolean): ResolvedTheme {
  if (choice !== 'system') return choice;
  // Dark unless the system says otherwise — §9's default, and the one a media query can be absent for.
  return systemPrefersLight ? 'light' : 'dark';
}

export function readThemeChoice(): ThemeChoice {
  let raw: string | null;
  try {
    raw = globalThis.localStorage.getItem(KEY);
  } catch {
    // Blocked or partitioned storage is not a boot failure — it is a reader with no saved choice.
    return 'dark';
  }
  // No saved choice is `dark`, not `system`: §9 promises a dark default, and on a light
  // desktop `system` resolves light. Following the system is still one explicit choice away.
  return raw === 'dark' || raw === 'light' || raw === 'system' ? raw : 'dark';
}

export function writeThemeChoice(choice: ThemeChoice): void {
  try {
    globalThis.localStorage.setItem(KEY, choice);
  } catch {
    // Nothing to recover: the theme still applies for this page load.
  }
}

/** Exported since 3e: the shell derives its icon from `(choice, system)` rather than storing a copy. */
export function systemPrefersLight(): boolean {
  return globalThis.matchMedia?.('(prefers-color-scheme: light)').matches === true;
}

export function currentTheme(): ResolvedTheme {
  return resolveTheme(readThemeChoice(), systemPrefersLight());
}

/** The swap. `<html data-theme>` rather than `<body>`: `color-scheme` on the root is what paints the
 * canvas behind an overscrolled page. */
export function applyTheme(theme: ResolvedTheme): void {
  document.documentElement.dataset['theme'] = theme;
  syncThemeColor();
}

/**
 * The OS's copy of the page background — only visible once the app is installed: it is
 * what an Android status bar, a desktop title bar and iOS's standalone chrome are painted with.
 *
 * Read back out of the *computed* custom property rather than passed in as a value, for the same
 * reason `.tsx` files never carry a colour: `--vs-color-bg` is the token that just changed, and a
 * second table mapping `'dark' → '#0b0d10'` here would be a copy to keep in step. The `<meta>` in
 * `index.html` carries the dark default so the first frame is right before this ever runs.
 *
 * Silent when there is nothing to read. A blank value would repaint the OS chrome white, and "the
 * stylesheet has not landed yet" is not a reason to tell the system anything.
 */
function syncThemeColor(): void {
  const meta = document.querySelector('meta[name="theme-color"]');
  if (!(meta instanceof HTMLMetaElement)) return;
  const value = getComputedStyle(document.documentElement).getPropertyValue('--vs-color-bg').trim();
  if (value !== '') meta.content = value;
}

/** Called once from `main.tsx`, before `createRoot`. */
export function initTheme(): ResolvedTheme {
  const theme = currentTheme();
  applyTheme(theme);
  return theme;
}

/**
 * Store a choice **and** apply it — the pair, because separating them was a real defect for exactly as
 * long as `writeThemeChoice` was the only public setter.
 *
 * The settings page writes the choice through `platform/settings.ts`, and storing without
 * applying would leave the reader looking at the old theme until they reloaded — with the setting
 * already showing the new one. The shell's toggle always called both; a second caller is what turned
 * "remember to do both" into a function.
 */
export function applyThemeChoice(choice: ThemeChoice): ResolvedTheme {
  writeThemeChoice(choice);
  const resolved = resolveTheme(choice, systemPrefersLight());
  applyTheme(resolved);
  return resolved;
}

/**
 * Follow the system. Without this, a reader whose OS switches to dark at sunset keeps the light theme
 * until they reload — and since the choice defaults to `'system'`, that is the common case rather than
 * the exotic one.
 *
 * It reports the *system* preference rather than a resolved theme, and fires **regardless of the current
 * choice** — 3e's correction. The previous version returned early unless the choice was `'system'`,
 * which was right while a copy of the resolved theme was the only state: nothing to update. Now the
 * shell derives the theme from `(choice, system)`, so swallowing the event leaves a stale `system` value
 * behind — and the reader who flips their desktop to dark while pinned to light, then chooses "follow
 * the system", gets light.
 */
export function subscribeSystemPrefersLight(onChange: (prefersLight: boolean) => void): () => void {
  const query = globalThis.matchMedia?.('(prefers-color-scheme: light)');
  if (query === undefined) return () => {};

  const listener = (): void => onChange(query.matches);
  query.addEventListener('change', listener);
  return () => query.removeEventListener('change', listener);
}
